import React, { useContext, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  IconButton,
  Input,
} from '@one-thd/sui-atomic-components';
import { ArrowForward, Close } from '@one-thd/sui-icons';
import { useAssistant } from './AssistantProvider';
import { dataModel } from './maaDataModel';
import { SuggestedQuestions } from './subcomponents/SuggestedQuestions';
import {
  keyPressMap,
  MESSAGE_ORIGIN_CUSTOM,
  MESSAGE_ORIGIN_PREPOPULATED,
  USER_QUESTION_CHARACTER_LIMIT
} from './constants';
import { DrawerContent } from './subcomponents/DrawerContent';
import { ScrollToBottomButton } from './subcomponents/drawer/ScrollToBottomButton';
import { useDrawer } from './DrawerProvider';

export const MagicApronAssistant = () => {
  const {
    historyLoading,
    askUserQuestion,
    botLoading: botResponseLoading,
    onAskAQuestionPromptClick,
  } = useAssistant();
  const { onScroll, scrollEl } = useDrawer();
  const [userMessage, setUserMessage] = useState('');
  const [focusInput, setFocusInput] = useState(false);

  const clearPrompt = () => setUserMessage('');

  const handleMessageSubmit = () => {
    if (userMessage.trim() === '' || botResponseLoading || historyLoading) return;

    askUserQuestion({ userQuestion: userMessage, messageOrigin: MESSAGE_ORIGIN_CUSTOM });
    clearPrompt();
  };

  const onUserQuestionChange = (event) => {
    if (!(keyPressMap.Enter)) setUserMessage(event.target.value);
  };

  const onUserQuestionKeyPress = (event) => {
    // Keeps track of all currently pressed keys
    keyPressMap[event.key] = event.type === 'keydown';

    if (keyPressMap.Enter) {
      handleMessageSubmit();
    }
  };

  // eslint-disable-next-line max-len
  const minimizedClasses = 'sui-overflow-y-hidden [@media(min-height:101px)]:sui-hidden [@media(max-height:100px)]:sui-block';
  // eslint-disable-next-line max-len
  const expandedClasses = '[@media(min-height:101px)]:sui-flex [@media(max-height:100px)]:sui-hidden sui-flex-col sui-justify-stretch sui-h-full';

  return (
    <div id="magic-apron-assistant" className="sui-h-full">
      <div className={minimizedClasses}>
        <SuggestedQuestions
          onClick={(userQuestion, suggestedQuestionPosition, isInitialSuggestedQuestion) => {
            setFocusInput(false);
            askUserQuestion({
              userQuestion,
              messageOrigin: MESSAGE_ORIGIN_PREPOPULATED,
              suggestedQuestionPosition,
              isInitialSuggestedQuestion,
            });
          }}
          withAskPrompt
          handleAskPromptClick={() => {
            setFocusInput(true);
            onAskAQuestionPromptClick();
          }}
          truncate
        />
      </div>
      <div className={expandedClasses}>
        <DrawerBody
          overflow="scroll"
          style={{ overscrollBehavior: 'contain' }}
          data-testid="drawer-body"
          ref={scrollEl}
          onScroll={onScroll}
        >
          <DrawerContent />
        </DrawerBody>
        <DrawerFooter>
          <ScrollToBottomButton />
          <div data-testid="input-box" className="sui-flex sui-justify-between sui-items-center sui-gap-2">
            <Input
              id="qa-bot-input"
              value={userMessage}
              onChange={onUserQuestionChange}
              onKeyDown={onUserQuestionKeyPress}
              onKeyUp={onUserQuestionKeyPress}
              autoFocus={focusInput}
              fullWidth
              multiline
              // On mobile minimized state the height of the textarea gets set to 0
              // this make sure that there is some height available
              inputProps={{
                style: { minHeight: '20px' }
              }}
              // based on how many lines are created by max character limit
              maxRows={8}
              placeholder="Ask a question"
              inputAttributes={{
                maxLength: USER_QUESTION_CHARACTER_LIMIT,
                resize: 'none',
                'aria-label': 'Assistant textbox',
              }}
              endAdornment={!!userMessage.length && (
                <IconButton
                  icon={Close}
                  size="small"
                  onClick={clearPrompt}
                  aria-label="Clear question"
                  key="clear-button"
                />
              )}
            />
            <div className="sui-grow-0">
              <Button
                variant="primary"
                onClick={handleMessageSubmit}
                id="magic_apron_qa_find_it_for_me"
                key="submit-button"
                aria-label="Submit question"
              >
                <ArrowForward color="inverse" />
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </div>
    </div>
  );
};

MagicApronAssistant.propTypes = {
};

MagicApronAssistant.defaultProps = {
};

MagicApronAssistant.dataModel = dataModel;

export default MagicApronAssistant;
